import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiInstagram, FiMail, FiUpload } from 'react-icons/fi';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const ArtistManager = () => {
  const [artists, setArtists] = useState([]);
  const [isAddingArtist, setIsAddingArtist] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    instagram: '',
    instagramUrl: '',
    email: '',
    image: null
  });

  useEffect(() => {
    loadArtists();
  }, []);

  const loadArtists = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'artists'));
      const artistsList = [];
      querySnapshot.forEach((doc) => {
        artistsList.push({ id: doc.id, ...doc.data() });
      });
      setArtists(artistsList);
    } catch (error) {
      console.error('Error loading artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddArtist = async () => {
    if (!formData.name) {
      alert('Please enter artist name');
      return;
    }
    
    try {
      const artistId = Date.now().toString();
      const newArtist = {
        ...formData,
        createdAt: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'artists', artistId), newArtist);
      await loadArtists();
      setIsAddingArtist(false);
      setFormData({ name: '', instagram: '', instagramUrl: '', email: '', image: null });
    } catch (error) {
      console.error('Error adding artist:', error);
      alert('Failed to add artist. Please try again.');
    }
  };

  const handleEditArtist = (artist) => {
    setFormData({
      name: artist.name,
      instagram: artist.instagram,
      instagramUrl: artist.instagramUrl,
      email: artist.email,
      image: artist.image
    });
    setEditingId(artist.id);
  };

  const handleUpdateArtist = async () => {
    try {
      await setDoc(doc(db, 'artists', editingId), formData);
      await loadArtists();
      setEditingId(null);
      setFormData({ name: '', instagram: '', instagramUrl: '', email: '', image: null });
    } catch (error) {
      console.error('Error updating artist:', error);
      alert('Failed to update artist. Please try again.');
    }
  };

  const handleDeleteArtist = async (id) => {
    if (window.confirm('Are you sure you want to delete this artist?')) {
      try {
        await deleteDoc(doc(db, 'artists', id));
        await loadArtists();
      } catch (error) {
        console.error('Error deleting artist:', error);
        alert('Failed to delete artist. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setIsAddingArtist(false);
    setEditingId(null);
    setFormData({ name: '', instagram: '', instagramUrl: '', email: '', image: null });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading artists...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <FiUsers className="text-2xl sm:text-3xl text-white" />
          <h2 className="text-xl sm:text-2xl font-bold text-white">Artists</h2>
        </div>
        
        {!isAddingArtist && !editingId && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsAddingArtist(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white text-slate-900 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm sm:text-base"
          >
            <FiPlus />
            <span>Add Artist</span>
          </motion.button>
        )}
      </div>

      {/* Add/Edit Artist Form */}
      <AnimatePresence>
        {(isAddingArtist || editingId) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 sm:mb-6 bg-white/15 rounded-xl p-4 sm:p-6 border border-white/30"
          >
            <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
              {editingId ? 'Edit Artist' : 'Add New Artist'}
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              {/* Artist Image */}
              <div>
                <label className="block text-white/90 mb-2 text-xs sm:text-sm font-medium">
                  Artist Photo
                </label>
                <div className="flex items-start gap-3 sm:gap-4">
                  <label className="flex-shrink-0 cursor-pointer">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-lg border-2 border-dashed border-white/30 hover:border-white/50 flex items-center justify-center overflow-hidden transition-all">
                      {formData.image ? (
                        <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <FiUpload className="text-2xl sm:text-3xl text-white/40" />
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-white/70 text-xs sm:text-sm mt-2">
                    Click to upload artist photo<br />
                    Recommended: 400x300px
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-white/90 mb-2 text-xs sm:text-sm font-medium">
                  Artist Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white/10 border border-white/30 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/50 text-sm sm:text-base"
                  placeholder="Enter artist name"
                  required
                />
              </div>

              <div>
                <label className="block text-white/90 mb-2 text-xs sm:text-sm font-medium flex items-center gap-2">
                  <FiInstagram />
                  Instagram Handle
                </label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className="w-full bg-white/10 border border-white/30 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/50 text-sm sm:text-base"
                  placeholder="@username"
                />
              </div>

              <div>
                <label className="block text-white/90 mb-2 text-xs sm:text-sm font-medium">
                  Instagram Profile URL
                </label>
                <input
                  type="url"
                  value={formData.instagramUrl}
                  onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                  className="w-full bg-white/10 border border-white/30 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/50 text-sm sm:text-base"
                  placeholder="https://www.instagram.com/username/"
                />
              </div>

              <div>
                <label className="block text-white/90 mb-2 text-xs sm:text-sm font-medium flex items-center gap-2">
                  <FiMail />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white/10 border border-white/30 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white placeholder-white/50 focus:outline-none focus:border-white/50 text-sm sm:text-base"
                  placeholder="artist@example.com"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                <button
                  onClick={editingId ? handleUpdateArtist : handleAddArtist}
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm sm:text-base"
                >
                  <FiSave />
                  <span>{editingId ? 'Update Artist' : 'Add Artist'}</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all text-sm sm:text-base"
                >
                  <FiX />
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Artists List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {artists.map((artist) => (
          <motion.div
            key={artist.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/15 rounded-xl p-3 sm:p-4 border border-white/30 hover:border-white/40 transition-all"
          >
            <div className="flex gap-3 sm:gap-4">
              <div className="flex-shrink-0">
                {artist.image ? (
                  <img
                    src={artist.image}
                    alt={artist.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white/10 rounded-lg flex items-center justify-center">
                    <FiUsers className="text-2xl sm:text-3xl text-white/40" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1.5 sm:mb-2">{artist.name}</h3>
                {artist.instagram && (
                  <p className="text-white/80 text-xs sm:text-sm mb-1 flex items-center gap-1.5">
                    <FiInstagram className="flex-shrink-0" />
                    <span className="truncate">{artist.instagram}</span>
                  </p>
                )}
                {artist.email && (
                  <p className="text-white/80 text-xs sm:text-sm mb-2 sm:mb-3 flex items-center gap-1.5">
                    <FiMail className="flex-shrink-0" />
                    <span className="truncate">{artist.email}</span>
                  </p>
                )}
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleEditArtist(artist)}
                    className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs sm:text-sm transition-all font-medium"
                  >
                    <FiEdit2 className="text-xs" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDeleteArtist(artist.id)}
                    className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded text-xs sm:text-sm transition-all font-medium"
                  >
                    <FiTrash2 className="text-xs" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {artists.length === 0 && !isAddingArtist && (
        <div className="text-center py-12 text-white/60">
          <FiUsers className="mx-auto text-4xl sm:text-5xl mb-3 sm:mb-4 opacity-50" />
          <p className="text-sm sm:text-base">No artists added yet. Click "Add Artist" to get started.</p>
        </div>
      )}
    </div>
  );
};

export default ArtistManager;